import type { HTMLProps } from 'react';
import { useState } from 'react';

import { useAuth0 } from '@auth0/auth0-react';
import { Typography } from '@remarkable/ark-web';
import { useForm } from 'react-hook-form';

import { InvalidEmailException } from 'src/api/endpoints/storeApi.types';
import {
  useAccountInfo,
  useCancelEmailChangeRequest,
  useEmailChangeStatus,
  useRequestEmailChange,
} from 'src/api/queries/account';
import {
  Button,
  Form,
  Input,
  Link,
  Modal,
  NotificationBox,
  Spinner,
} from 'src/components';
import { userHelpers } from 'src/utils/userHelpers';

import { Trans, useLingui } from '@lingui/react/macro';
import { ComponentLocations } from 'src/analytics/trackingTypes';
import { NoUserError } from 'src/utils/noUserError';
import { EditEmailModal } from './EditEmailModal';

interface EmailForm {
  email: string;
}

export const EditEmailForm = (props: HTMLProps<HTMLDivElement>) => {
  const { t } = useLingui();
  const { user } = useAuth0();
  const requestEmailChange = useRequestEmailChange();
  const emailChangeStatus = useEmailChangeStatus();
  const cancelEmailChange = useCancelEmailChangeRequest();
  const [isEditable, setIsEditable] = useState(false);
  const accountInfo = useAccountInfo();

  const form = useForm<EmailForm>({
    defaultValues: {
      email: user?.email ?? '',
    },
  });

  const onSubmit = (data: EmailForm) => {
    if (data.email === user?.email) {
      setIsEditable(false);
      form.reset();
      return;
    }

    requestEmailChange.mutate(
      {
        currentEmail: user?.email ?? '',
        newEmail: data.email,
      },
      {
        onSuccess: () => {
          setIsEditable(false);
          form.reset();
          return emailChangeStatus.refetch();
        },
      }
    );
  };

  const onCancelEmailChangeRequest = () => {
    cancelEmailChange.mutate(undefined, {
      onSuccess: () => emailChangeStatus.refetch(),
    });
  };

  if (!user) {
    throw new NoUserError('No user found when loading edit email form');
  }

  const emailDomain = userHelpers.getEmail(user).split('@')[1];
  const isDomainClaimed = userHelpers.isDomainClaimedByConnection(user);
  const identities = accountInfo.data?.identities ?? [];

  const allowedToEdit = userHelpers.canEditEmail(user, identities);

  // Social accounts are managed by the provider, so we don't allow
  // the user to change their email address from myrm
  const isPendingEmailChange = emailChangeStatus.data?.status === 'pending';
  const newEmail = emailChangeStatus.data?.newEmail ?? '';

  return (
    <div {...props}>
      <Form onSubmit={onSubmit} {...form}>
        <div className="flex flex-col gap-16 lm:flex-row">
          <Input
            label={t`Email`}
            name="email"
            type="email"
            required
            isEditable={isEditable}
            focusOnIsEditable
            data-hj-suppress
            data-cy="email-field"
          />
          {!isEditable && allowedToEdit && (
            <>
              {accountInfo.data?.identities &&
              accountInfo.data.identities.length > 1 ? (
                <Modal.Root>
                  <Modal.Trigger asChild>
                    <Button
                      variant="tertiary"
                      size="medium"
                      className="w-full self-start ls:w-fit"
                      data-cy="edit-email-button-multiple-identities"
                    >
                      <Trans>Edit</Trans>
                    </Button>
                  </Modal.Trigger>
                  <EditEmailModal />
                </Modal.Root>
              ) : (
                <Button
                  variant="tertiary"
                  type="button"
                  size="medium"
                  onClick={() => setIsEditable(true)}
                  className="w-full self-start ls:w-fit"
                  data-cy="edit-email-button"
                >
                  <Trans>Edit</Trans>
                </Button>
              )}
            </>
          )}
        </div>

        {isEditable && (
          <div className="my-16 flex flex-col gap-16">
            {isDomainClaimed && (
              <NotificationBox
                variant="info"
                title={t`You'll also have access to your enterprise account`}
              >
                <Trans>
                  If you change your email address, we&apos;ll create a separate
                  enterprise account for your @{emailDomain} email. You can move
                  files to that account later.
                </Trans>
              </NotificationBox>
            )}

            <div className="flex gap-16">
              <Button
                onClick={() => {
                  setIsEditable(false);
                  requestEmailChange.reset();
                  form.reset();
                }}
                type="button"
                variant="secondary"
                data-cy="cancel-email-button"
                size="medium"
              >
                <Trans>Cancel</Trans>
              </Button>
              <Button
                type="submit"
                variant="primary"
                loading={requestEmailChange.isPending}
                data-cy="save-email-button"
                size="medium"
              >
                <Trans>Save</Trans>
              </Button>
            </div>
          </div>
        )}

        {isPendingEmailChange && emailChangeStatus.data && (
          <NotificationBox
            title={t`Verify your new email address`}
            variant="warning"
            className="my-24"
          >
            <Typography variant="body-sm" as="p">
              <Trans>
                A verification email has been sent to:{' '}
                <Typography variant="body-sm" fontWeight="bold" as="strong">
                  {newEmail}
                </Typography>
              </Trans>
            </Typography>
            <Typography variant="body-sm" as="p">
              <Trans>
                Follow the instructions to confirm your new email address. The
                link is valid for 30 minutes.
              </Trans>
            </Typography>
            <div className="mt-16 flex gap-16">
              <Link
                onClick={onCancelEmailChangeRequest}
                as="button"
                inline
                className="body-md"
                trackingLocation={ComponentLocations.ACCOUNT.CONTACT_DETAILS}
              >
                {cancelEmailChange.isPending ? (
                  <Spinner inline />
                ) : (
                  <Trans>Cancel</Trans>
                )}
              </Link>
              <Link
                onClick={() => onSubmit({ email: newEmail })}
                className="body-md"
                as="button"
                inline
                trackingLocation={ComponentLocations.ACCOUNT.CONTACT_DETAILS}
              >
                {requestEmailChange.isPending ? (
                  <Spinner inline />
                ) : (
                  <Trans>Resend</Trans>
                )}
              </Link>
            </div>
          </NotificationBox>
        )}

        {(requestEmailChange.isError || cancelEmailChange.isError) && (
          <NotificationBox
            error={[requestEmailChange.error, cancelEmailChange.error]}
            className="my-24"
            data-cy="email-change-error"
          >
            <Typography variant="body-sm" as="p">
              {requestEmailChange.error instanceof InvalidEmailException ? (
                requestEmailChange.error.message
              ) : (
                <Trans>
                  Please try again later or contact support if the problem
                  persists.
                </Trans>
              )}
            </Typography>
          </NotificationBox>
        )}
      </Form>
    </div>
  );
};
